import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import { RelatorioMensal } from '../models/relatorio';
import maestroService from '../maestroService';
import { formatarData, formatarPercentual } from '../utils/formatters';
import { LABEL_AREA } from '../utils/labels';
import Card from '../components/Card';
import EstadoErro from '../components/EstadoErro';
import EstadoVazio from '../components/EstadoVazio';
import Header from '../components/Header';
import { SkeletonCard } from '../components/Skeletons';

const MaestroRelatorioPage: React.FC = () => {
  const { cicloAtivo, empresasEscopo, pontuacoes, getArea } = useMaestroContext();
  const [modoReuniao, setModoReuniao] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [r, setR] = useState<RelatorioMensal | null>(null);

  const carregarRelatorio = () => {
    if (!cicloAtivo.id) { setCarregando(false); return; }
    setCarregando(true);
    setErro(null);
    maestroService.obterRelatorioMensal(cicloAtivo.id, cicloAtivo, pontuacoes)
      .then(setR)
      .catch((e: any) => setErro(e?.message ?? 'Não foi possível carregar o relatório mensal.'))
      .finally(() => setCarregando(false));
  };

  useEffect(carregarRelatorio, [cicloAtivo.id]);

  if (carregando) {
    return (
      <div className='mc-page'>
        <Header title='Relatório Mensal de Fechamento' subtitle='Carregando...' />
        <SkeletonCard />
      </div>
    );
  }

  if (erro) {
    return (
      <div className='mc-page'>
        <Header title='Relatório Mensal de Fechamento' />
        <Card><EstadoErro descricao={erro} onTentarNovamente={carregarRelatorio} /></Card>
      </div>
    );
  }

  if (!r) {
    return (
      <div className='mc-page'>
        <Header title='Relatório Mensal de Fechamento' />
        <Card>
          <EstadoVazio
            icone='bi bi-file-earmark-bar-graph'
            titulo='Nenhum relatório gerado para este ciclo ainda'
            descricao='Gere o relatório mensal a partir do ciclo ativo para visualizar a pré-visualização aqui.'
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={`mc-page mc-relatorio${modoReuniao ? ' mc-relatorio--reuniao' : ''}`}>
      <Header
        title='Relatório Mensal de Fechamento'
        subtitle={`Pré-visualização — competência ${r.competencia} — ${r.escopoResumo}`}
        actions={<>
          <button type='button' className='mc-btn mc-btn--secondary' onClick={() => window.print()}>Imprimir</button>
          <button type='button' className='mc-btn mc-btn--primary' onClick={() => setModoReuniao(v => !v)}>{modoReuniao ? 'Sair do modo reunião' : 'Abrir modo reunião'}</button>
        </>}
      />

      <div className='mc-grid-2'>
        <Card title='Resumo do ciclo'>
          <dl className='mc-drawer__resumo'>
            <div><dt>Empresas</dt><dd>{empresasEscopo.map(e => e.nome).join(', ')}</dd></div>
            <div><dt>Prazo oficial</dt><dd>{formatarData(r.prazoOficial)}</dd></div>
            <div><dt>Resultado do ciclo</dt><dd>{r.resultadoCiclo}</dd></div>
            <div><dt>Progresso</dt><dd>{formatarPercentual(r.progresso)}</dd></div>
            <div><dt>Entregas no prazo</dt><dd>{r.entregasNoPrazo}</dd></div>
            <div><dt>Entregas antecipadas</dt><dd>{r.entregasAntecipadas}</dd></div>
            <div><dt>Entregas atrasadas</dt><dd>{r.entregasAtrasadas}</dd></div>
            <div><dt>Retrabalhos</dt><dd>{r.retrabalhos}</dd></div>
          </dl>
        </Card>
        <Card title='Comparação com o ciclo anterior'>
          <table className='mc-logs-table'>
            <thead><tr><th>Indicador</th><th>Atual</th><th>Anterior</th></tr></thead>
            <tbody>
              {r.comparacaoCicloAnterior.map(c => <tr key={c.indicador}><td>{c.indicador}</td><td className='mc-num'>{c.atual}</td><td className='mc-num'>{c.anterior}</td></tr>)}
            </tbody>
          </table>
        </Card>
      </div>

      <Card title='Principais dependências'>
        <ul className='mc-wizard-lista'>{r.principaisDependencias.map((d, i) => <li key={i}>{d}</li>)}</ul>
      </Card>

      <div className='mc-grid-2'>
        <Card title='Áreas que mais contribuíram'>
          <ul className='mc-wizard-lista'>{r.areasQueMaisContribuiramIds.map(id => { const a = getArea(id); return a ? <li key={id}>{LABEL_AREA[a.tipo]}</li> : null; })}</ul>
        </Card>
        <Card title='Áreas que exigem atenção'>
          <ul className='mc-wizard-lista'>{r.areasQueExigemAtencaoIds.map(id => { const a = getArea(id); return a ? <li key={id}>{LABEL_AREA[a.tipo]}</li> : null; })}</ul>
        </Card>
      </div>

      <Card title='Principais atrasos e causas'>
        <ul className='mc-wizard-lista'>{r.principaisAtrasos.map((a, i) => <li key={i}><strong>{a.atividade}:</strong> {a.causa}</li>)}</ul>
      </Card>

      <div className='mc-grid-2'>
        <Card title='Achados e impactos'>
          <dl className='mc-drawer__resumo'>
            <div><dt>Achados do Revisor Contábil</dt><dd>{r.achadosRevisor}</dd></div>
            <div><dt>Impactos fiscais</dt><dd>{r.impactosFiscais}</dd></div>
            <div><dt>Impactos financeiros</dt><dd>{r.impactosFinanceiros}</dd></div>
            <div><dt>Variações no Mapa de Resultados</dt><dd>{r.variacoesMapaResultados}</dd></div>
          </dl>
        </Card>
        <Card title='Situação dos produtos integrados'>
          <dl className='mc-drawer__resumo'>
            <div><dt>Conciliações</dt><dd>{r.situacaoConciliacoes}</dd></div>
            <div><dt>Controles SOX</dt><dd>{r.situacaoControlesSox}</dd></div>
          </dl>
        </Card>
      </div>

      <p className='mc-texto-neutro mc-relatorio__rodape'>Gerado em {formatarData(r.geradoEm)} · Ciclo {cicloAtivo.competencia}</p>
    </div>
  );
};

export default MaestroRelatorioPage;
